import { camelCase, snakeCase, capitalCase } from "change-case";

const RAW_KEYS = new Set(["uniqueProps", "mapData"]);

/**
 * Recursively walk an object/array and convert all keys using `keyConverter`.
 * Skips recursion for values of keys listed in RAW_KEYS (by original or converted name).
 */
function convertKeys(obj: any, keyConverter: (key: string) => string): any {
  if (obj === null || typeof obj !== "object") return obj;

  if (Array.isArray(obj)) {
    return obj.map((v) => convertKeys(v, keyConverter));
  }

  const isMatrixDynamic = obj.type === "matrixdynamic";

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      const newKey = keyConverter(String(key));
      const shouldSkipRecursion =
        RAW_KEYS.has(key) ||
        RAW_KEYS.has(newKey) ||
        (isMatrixDynamic && key === "value");

      const newValue = shouldSkipRecursion
        ? value
        : convertKeys(value, keyConverter);

      return [newKey, newValue];
    })
  );
}

/** Deep convert keys: camelCase / mixed → snake_case */
export const convertKeysToSnakeCase = (obj: any): any =>
  convertKeys(obj, (key) => snakeCase(key));

/** Deep convert keys: snake_case / kebab-case / mixed → camelCase */
export const convertKeysToCamelCase = (obj: any): any =>
  convertKeys(obj, (key) => camelCase(key));

/** Single string: snake_case / kebab-case / mixed → camelCase */
export function snakeToCamel(s: string): string {
  return camelCase(s);
}

/** Single string: camelCase / mixed → snake_case */
export function camelToSnake(s: string): string {
  return snakeCase(s);
}

/** Single string: camelCase → Title Case (“fooBarBaz” → “Foo Bar Baz”) */
export function camelCaseToTitleCase(s: string): string {
  // capitalCase inserts spaces and capitalizes words
  return capitalCase(s);
}

/** Single string alias: underscore_separated → camelCase */
export function toCamelCase(s: string): string {
  return camelCase(s);
}
